'use client';

import { FC, Fragment } from 'react';
import * as prismic from '@prismicio/client';
import { Content, asLink, isFilled } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import SocialMediaLogo from '@/assets/logos/SocialMediaLogo';

type LeadershipSlice = prismic.SharedSlice<
  'team_members_grid',
  Content.TeamMembersGridSliceLeadership
>;

type Props = {
  slice: LeadershipSlice;
};

type Member = NonNullable<LeadershipSlice['primary']['members']>[number];

const COLS = 3;

const CARD_HOVER_GRADIENT =
  'linear-gradient(180deg, rgba(75,15,234,0.22) 0%, rgba(168,135,255,0.04) 100%)';

const ROW_COL_CLASS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-[1fr_1px_1fr]',
  3: 'lg:grid-cols-[1fr_1px_1fr_1px_1fr]',
};

const DIVIDER_GRADIENT_V =
  'linear-gradient(to bottom, rgba(168,135,255,0) 0%, rgba(168,135,255,1) 6%, rgba(168,135,255,1) 94%, rgba(168,135,255,0) 100%)';
const DIVIDER_GRADIENT_H =
  'linear-gradient(to right, rgba(168,135,255,0) 0%, rgba(168,135,255,1) 6%, rgba(168,135,255,1) 94%, rgba(168,135,255,0) 100%)';

const Leadership: FC<Props> = ({ slice }) => {
  const { heading, description, members } = slice.primary;
  const eyebrow = (slice.primary as { eyebrow?: string }).eyebrow;

  const list: Member[] = members ?? [];
  const rows: { items: Member[]; startIndex: number }[] = [];
  for (let i = 0; i < list.length; i += COLS) {
    rows.push({ items: list.slice(i, i + COLS), startIndex: i });
  }

  const renderCard = (member: Member, globalIndex: number) => {
    const isPriority = globalIndex < 3;

    const firstSocial = member.social_links?.find((link) =>
      isFilled.link(link),
    );
    const firstSocialUrl = firstSocial ? asLink(firstSocial) : null;

    return (
      <div className="group relative flex min-h-[280px] cursor-default flex-col items-center justify-start gap-[14px] px-[24px] py-[32px] text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: CARD_HOVER_GRADIENT }}
        />
        <div className="relative z-[1] flex w-full flex-1 flex-col items-center gap-[14px]">
          {isFilled.image(member.photo) && (
            <div className="leadership-avatar flex size-[120px] shrink-0 items-center justify-center overflow-hidden rounded-full border-[3px] border-transparent bg-[lightgray] transition-colors duration-300 group-hover:border-[#00F557] group-hover:bg-[#00F557] md:size-[128px]">
              <PrismicNextImage
                field={member.photo}
                className="h-full w-full object-cover object-top"
                loading={isPriority ? 'eager' : 'lazy'}
                fetchPriority={isPriority ? 'high' : undefined}
                decoding={isPriority ? 'auto' : 'async'}
                imgixParams={{
                  w: 256,
                  h: 256,
                  fit: 'crop',
                  crop: ['faces', 'top'],
                  auto: ['compress', 'format'],
                  q: 70,
                }}
                sizes="128px"
                fallbackAlt=""
              />
            </div>
          )}

          {member.name && (
            <h3 className="text-[18px] font-bold leading-[1.3] text-white md:text-[20px]">
              {member.name}
            </h3>
          )}

          {member.role && (
            <p className="-mt-[6px] max-w-[220px] whitespace-pre-line text-[13px] leading-[1.4] text-white/70 md:text-[14px]">
              {member.role}
            </p>
          )}

          {member.subtitle && (
            <p className="text-[12px] leading-[1.4] text-white/55">
              {member.subtitle}
            </p>
          )}

          {firstSocialUrl && (
            <div className="mt-auto pt-2">
              <a
                href={firstSocialUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${member.name ?? 'Team member'} on LinkedIn`}
                className="inline-flex opacity-80 transition-opacity duration-200 hover:opacity-100"
              >
                <SocialMediaLogo platform="linkedin" className="h-6 w-6" />
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white px-[30px] py-12 md:py-16"
    >
      <div
        className="w-full overflow-hidden rounded-[24px] px-6 py-12 md:px-10 md:py-14"
        style={{
          background:
            'radial-gradient(55% 40% at 50% 45%, rgba(75,15,234,0.22) 0%, rgba(33,0,124,0.08) 55%, rgba(1,6,10,0) 100%), #01060a',
        }}
      >
        <div className="mb-10 flex flex-col items-center gap-5 text-center md:mb-14">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#00f557] px-5 py-2 text-[12px] font-normal uppercase leading-[1.2] tracking-[0.12em] text-[#00f557] md:text-[13px]">
              {eyebrow}
            </span>
          )}

          {isFilled.richText(heading) && (
            <PrismicRichText
              field={heading}
              components={{
                heading2: ({ children }) => (
                  <h2 className="max-w-[1040px] text-[32px] font-bold leading-[1.15] text-white md:text-[44px]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {isFilled.richText(description) && (
            <div className="max-w-[680px]">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-[14px] leading-[1.5] text-white/70 md:text-[16px]">
                      {children}
                    </p>
                  ),
                }}
              />
            </div>
          )}
        </div>

        {list.length > 0 && (
          <div className="mx-auto w-full max-w-[1080px]">
            {rows.map((row, rowIdx) => {
              const isLastRow = rowIdx === rows.length - 1;
              const itemCount = row.items.length;
              const colsClass = ROW_COL_CLASS[itemCount] ?? 'lg:grid-cols-3';

              return (
                <div
                  key={`row-${rowIdx}`}
                  className={`relative grid grid-cols-1 sm:grid-cols-2 ${colsClass}`}
                >
                  {row.items.map((member, colIdx) => {
                    const globalIndex = row.startIndex + colIdx;
                    return (
                      <Fragment key={globalIndex}>
                        {colIdx > 0 && (
                          <div
                            aria-hidden
                            className="hidden lg:block"
                            style={{ background: DIVIDER_GRADIENT_V }}
                          />
                        )}
                        {renderCard(member, globalIndex)}
                      </Fragment>
                    );
                  })}

                  {!isLastRow && (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-x-0 bottom-0 z-10 hidden h-px lg:block"
                      style={{ background: DIVIDER_GRADIENT_H }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default Leadership;
